import React from "react";
import {
  X,
  FileText,
  Building2,
  Box,
  CalendarDays,
  CreditCard,
  CheckCircle2,
  Users,
  Link,
  ShieldCheck,
  Calculator,
  IndianRupee,
} from "lucide-react";

// Helpers
const formatCurrency = (amount) => {
  if (!amount) return "₹0";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(dateString));
};

export default function QuotationReportModal({ isOpen, onClose, quotation }) {
  if (!isOpen || !quotation) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-gray-900/60 backdrop-blur-sm font-sans">
      {/* Modal Container */}
      <div className="w-full max-w-5xl bg-gray-50 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[95vh] animate-in fade-in zoom-in-95 duration-200">
        {/* Modal Header */}
        <div className="bg-gray-900 px-6 py-4 flex items-center justify-between sticky top-0 z-10 shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/20 text-blue-300 rounded-lg">
              <FileText size={24} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white tracking-wide">
                Quotation Report
              </h2>
              <p className="text-gray-400 text-sm flex items-center gap-2">
                {quotation.quotation_no} •{" "}
                {formatDate(quotation.quotation_date)}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-full transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Modal Body (Scrollable) */}
        <div className="p-6 md:p-8 overflow-y-auto space-y-8 flex-1">
          {/* Top Quick Info Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-start gap-3">
              <Building2 className="text-blue-500 shrink-0 mt-0.5" size={20} />
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase">
                  Client
                </p>
                <p className="font-medium text-gray-900">
                  {quotation.client_name}
                </p>
              </div>
            </div>
            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-start gap-3">
              <Box className="text-blue-500 shrink-0 mt-0.5" size={20} />
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase">
                  Software Type
                </p>
                <p className="font-medium text-gray-900">
                  {quotation.software_type}
                </p>
              </div>
            </div>
            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-start gap-3">
              <CalendarDays
                className="text-orange-500 shrink-0 mt-0.5"
                size={20}
              />
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase">
                  Delivery Date
                </p>
                <p className="font-medium text-gray-900">
                  {formatDate(quotation.software_delivery_date)}
                </p>
              </div>
            </div>
            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-start gap-3">
              <ShieldCheck
                className="text-green-500 shrink-0 mt-0.5"
                size={20}
              />
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase">
                  Domain Management
                </p>
                <p className="font-medium text-gray-900">
                  {quotation.domain_managed_by}
                </p>
              </div>
            </div>
          </div>

          {/* Access & Login Roles (Pill Badges) */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Users size={20} className="text-gray-500" />
              Access & Login Roles
            </h3>
            <div className="flex flex-wrap items-center gap-2.5">
              {quotation.login_types?.length > 0 ? (
                quotation.login_types.map((login) => (
                  <span
                    key={login.id}
                    className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-full text-sm font-medium shadow-sm"
                  >
                    <CheckCircle2 size={16} className="text-blue-500" />
                    {login.login_type}
                  </span>
                ))
              ) : (
                <span className="text-sm text-gray-500 italic">
                  No login roles specified.
                </span>
              )}
            </div>
          </div>

          {/* Payment Terms Table */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <CreditCard size={20} className="text-gray-500" />
              Payment Pay Term
            </h3>
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
              <table className="w-full text-left border-collapse">
                <thead className="bg-gray-900 text-white">
                  <tr>
                    <th className="px-5 py-3 text-sm font-medium tracking-wide">
                      Installment
                    </th>
                    <th className="px-5 py-3 text-sm font-medium tracking-wide text-right">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {quotation.payment_terms?.length > 0 ? (
                    quotation.payment_terms.map((term) => (
                      <tr
                        key={term.id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-5 py-3.5 text-sm text-gray-800 font-medium">
                          {term.term_title}
                        </td>
                        <td className="px-5 py-3.5 text-sm text-gray-900 font-semibold text-right">
                          {formatCurrency(term.amount)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="2"
                        className="px-5 py-6 text-center text-gray-500 text-sm"
                      >
                        No payment terms specified.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* IDsPay API Integrations Table */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Link size={20} className="text-indigo-500" />
              IDsPay API Integrations
            </h3>
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
              <table className="w-full text-left border-collapse">
                <thead className="bg-gray-900 text-white">
                  <tr>
                    <th className="px-5 py-3 text-sm font-medium tracking-wide">
                      Service Name
                    </th>
                    <th className="px-5 py-3 text-sm font-medium tracking-wide text-right">
                      Call Charge
                    </th>
                    <th className="px-5 py-3 text-sm font-medium tracking-wide text-right">
                      Integration Fee
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 text-sm">
                  {quotation.idspay_apis?.length > 0 ? (
                    quotation.idspay_apis.map((api) => (
                      <tr
                        key={`ids-${api.id}`}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-5 py-3.5 text-gray-900 font-medium">
                          {api.api_service_name}
                        </td>
                        <td className="px-5 py-3.5 text-gray-600 text-right">
                          {formatCurrency(api.api_call_charge)}
                        </td>
                        <td className="px-5 py-3.5 text-gray-900 font-semibold text-right">
                          {formatCurrency(api.integration_fee)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="3"
                        className="px-5 py-6 text-center text-gray-500"
                      >
                        No IDsPay APIs included.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Third-Party API Integrations Table */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Link size={20} className="text-orange-500" />
              Third-Party API Integrations
            </h3>
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
              <table className="w-full text-left border-collapse">
                <thead className="bg-gray-900 text-white">
                  <tr>
                    <th className="px-5 py-3 text-sm font-medium tracking-wide">
                      Service Name
                    </th>
                    <th className="px-5 py-3 text-sm font-medium tracking-wide text-right">
                      Integration Fee
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 text-sm">
                  {quotation.third_party_apis?.length > 0 ? (
                    quotation.third_party_apis.map((api) => (
                      <tr
                        key={`tp-${api.id}`}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-5 py-3.5 text-gray-900 font-medium">
                          {api.api_service_name}
                        </td>
                        <td className="px-5 py-3.5 text-gray-900 font-semibold text-right">
                          {formatCurrency(api.integration_fee)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="2"
                        className="px-5 py-6 text-center text-gray-500"
                      >
                        No Third-Party APIs included.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Final Cost Summary Table */}
          <div className="pt-4 border-t border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Calculator size={20} className="text-blue-600" />
              Final Cost Breakdown & Summary
            </h3>

            <div className="bg-white rounded-xl border border-gray-300 shadow-md overflow-hidden">
              <table className="w-full text-left border-collapse">
                <thead className="bg-gray-900 text-white">
                  <tr>
                    <th className="px-6 py-4 font-semibold text-base">
                      Description
                    </th>
                    <th className="px-6 py-4 font-semibold text-base text-right">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-gray-700">
                      Base Software Cost
                    </td>
                    <td className="px-6 py-4 text-gray-900 font-medium text-right">
                      {formatCurrency(quotation.software_cost)}
                    </td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-gray-700">
                      IDsPay API Integration Total
                    </td>
                    <td className="px-6 py-4 text-gray-900 font-medium text-right">
                      {formatCurrency(quotation.idspay_api_integration_cost)}
                    </td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-gray-700">
                      Third-Party API Integration Total
                    </td>
                    <td className="px-6 py-4 text-gray-900 font-medium text-right">
                      {formatCurrency(
                        quotation.third_party_api_integration_cost,
                      )}
                    </td>
                  </tr>
                  <tr className="hover:bg-gray-50 bg-blue-50/30">
                    <td className="px-6 py-4 text-gray-700">
                      Annual Maintenance Cost (AMC)
                    </td>
                    <td className="px-6 py-4 text-gray-900 font-medium text-right">
                      {formatCurrency(quotation.annual_maintenance_cost)}
                    </td>
                  </tr>
                  {parseFloat(quotation.discount_applicable) > 0 && (
                    <tr className="bg-green-50/50">
                      <td className="px-6 py-4 text-green-700 font-medium">
                        Discount Applied
                      </td>
                      <td className="px-6 py-4 text-green-700 font-bold text-right">
                        - {formatCurrency(quotation.discount_applicable)}
                      </td>
                    </tr>
                  )}
                  {/* Grand Total Row */}
                  <tr className="bg-gray-900 text-white">
                    <td className="px-6 py-5 text-lg font-bold">
                      Net Total Cost
                    </td>
                    <td className="px-6 py-5 text-xl font-bold text-right flex items-center justify-end gap-1">
                      <IndianRupee
                        size={20}
                        strokeWidth={2.5}
                        className="mt-0.5"
                      />
                      {formatCurrency(quotation.net_total_cost).replace(
                        "₹",
                        "",
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
              <div className="bg-slate-50 px-6 py-5 border-t border-gray-200">
                <div className="flex items-start gap-3">
                  {/* Optional: Add an icon here, like Info or FileText from lucide-react */}
                  <div className="p-1.5 bg-slate-200/50 rounded-md shrink-0">
                    <FileText size={16} className="text-slate-600" />
                  </div>

                  <div className="flex-1 w-full">
                    <h4 className="font-semibold text-slate-800 text-sm mb-1.5">
                      Additional Notes & Details
                    </h4>

                    {quotation.quotation_details ? (
                      <div
                        className="text-sm text-slate-600 leading-relaxed 
                     p-5 bg-white max-w-none
        [&_ul]:list-disc
        [&_ul]:pl-6
        [&_ol]:list-decimal
        [&_ol]:pl-6
        [&_li]:ml-1

          [&_h1]:text-3xl
        [&_h1]:font-bold
        [&_h1]:mb-4

        [&_h2]:text-2xl
        [&_h2]:font-bold
        [&_h2]:mb-3

        [&_h3]:text-xl
        [&_h3]:font-bold
        [&_h3]:mb-3

        [&_h4]:text-lg
        [&_h4]:font-bold
        [&_h4]:mb-2

        [&_h5]:text-sm
        [&_h5]:font-bold
        [&_h5]:mb-2
                     [&>strong]:font-semibold [&>strong]:text-slate-900
                     [&>a]:text-indigo-600 [&>a]:hover:underline"
                        dangerouslySetInnerHTML={{
                          __html: quotation.quotation_details,
                        }}
                      />
                    ) : (
                      <p className="text-sm text-slate-400 italic">
                        No additional notes provided for this quotation.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="bg-white border-t border-gray-200 px-6 py-4 flex justify-end gap-3 shrink-0">
          <button
            onClick={onClose}
            className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Close Report
          </button>
          <button className="px-5 py-2.5 hidden text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 shadow-sm transition-colors flex items-center gap-2">
            <FileText size={16} />
            Download PDF
          </button>
        </div>
      </div>
    </div>
  );
}
